/* eslint-disable max-len */
/**
 * Dependencies:
 * - Custom select component
 * - Fetch cache (theme.fetchCache)
 * - Quickbuy content transformer (theme.quickBuy.convertToQuickBuyContent)
 *
 * Required translation strings:
 * - noStock
 * - noVariant
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.quickBuyContainer = this.closest('.js-quickbuy');
      this.featuredProductContainer = this.closest('.cc-featured-product');
      this.section = this.quickBuyContainer
                     || this.featuredProductContainer
                     || this.closest('.shopify-section');
      // RM: linked companion dropdowns (color family grouping) carry .option-selector--custom
      // and [data-selector-ignore], so they are not counted as options.
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom):not([data-selector-ignore])');
      this.productAvailable = this.classList.contains('variant-picker--product-available');
      this.variant = this.getVariantData();
      this.variantRequestId = 0;

      this.addListeners();
      this.updateVariantContent();
      if (!this.quickBuyContainer && !this.featuredProductContainer) {
        setTimeout(this.applySearchParams.bind(this), 0);
      }
      this.setAttribute('loaded', '');
    }

    addListeners() {
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.addEventListener('change', this.boundHandleVariantChange);

      this.boundHandleLabelMouseEnter = this.handleLabelMouseEnter.bind(this);
      this.querySelectorAll('.opt-label, .custom-select__option').forEach((el) => {
        el.addEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.addEventListener('touchstart', this.boundHandleLabelMouseEnter, { passive: true, once: true });
        el.addEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
      });
    }

    removeListeners() {
      this.removeEventListener('change', this.boundHandleVariantChange);
      this.querySelectorAll('.opt-label, .custom-select__option').forEach((el) => {
        el.removeEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.removeEventListener('touchstart', this.boundHandleLabelMouseEnter);
        el.removeEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
      });
    }

    disconnectedCallback() {
      // RM: stop watching for Klaviyo once this picker has been swapped out.
      this.bisObserver?.disconnect();
    }

    /**
     * Update parts of this component that require JS to display correctly.
     */
    updateVariantContent() {
      this.updateStatusClasses();
      this.updateAddToCartButton();
      // RM: must stay adjacent to updateAddToCartButton(). That call marks the button disabled
      // for an unavailable variant; overriding it before anything reads layout means the browser
      // only ever computes the final state (no stray .btn transition).
      this.updateBISButton();
      this.updateQuantityState();
      this.updateSwatchLabel();
      this.updateAvailability();
      this.initColorFamilies();
    }

    getProductForm() {
      return this.section.querySelector('.js-product-form');
    }

    /**
     * Handles 'mouseenter' events on option labels. Preloads links.
     * @param {object} evt - Event object.
     */
    handleLabelMouseEnter(evt) {
      const label = evt.currentTarget;
      if (label.dataset.preloaded) return; // Preload completed
      if (label.dataset.preloadTimeout) return; // Preload waiting
      if (label.matches('.opt-btn:checked + .opt-label, [aria-selected="true"]')) return; // Already selected

      // Build option array if clicked
      const input = label.htmlFor ? document.getElementById(label.htmlFor) : label;
      const optionIds = this.getSelectedOptionIds();
      const inputOptionIndex = Array.from(this.optionSelectors).indexOf(this.getListedSelector(input.closest('.option-selector')));
      if (inputOptionIndex === -1) return;
      optionIds[inputOptionIndex] = input.dataset.valueId;

      // If all options are selected
      if (optionIds.indexOf(null) === -1) {
        // Wait to predict click-intent and preload link
        label.dataset.preloadTimeout = setTimeout(() => {
          label.dataset.preloaded = true;
          label.removeAttribute('data-preload-timeout');
          const combinedProductUrl = input.dataset.productUrl;
          const url = this.constructVariantUrl(
            combinedProductUrl,
            optionIds,
            input.dataset.variantId
          );
          theme.fetchCache.preload(url.toString());
        }, 250);
      }
    }

    /**
     * Handles 'mouseleave' events on option labels. Cancels link preload if in/out fast.
     * @param {object} evt - Event object.
     */
    static handleLabelMouseLeave(evt) {
      const label = evt.currentTarget;
      clearTimeout(label.dataset.preloadTimeout);
      label.removeAttribute('data-preload-timeout');
    }

    /**
     * Create a URL for fetching a product or variant.
     * @param {string} combinedProductUrl - URL for a combined product. Optional.
     * @param {Array} optionIds - Selected option IDs to include in the URL. Optional.
     * @param {string} variantId - Variant ID to use if not a combined product. Optional.
     * @returns {URL} Variant URL
     */
    constructVariantUrl(combinedProductUrl, optionIds, variantId) {
      let url = null;
      if (combinedProductUrl) {
        url = new URL(combinedProductUrl, window.location.origin);
        url.searchParams.set('option_values', optionIds.join(','));
      } else {
        url = new URL(this.dataset.url, window.location.origin);
      }
      const sectionIds = [this.dataset.sectionId];
      if (!this.quickBuyContainer && !this.featuredProductContainer) {
        document.querySelectorAll('.cc-variant-dependent-section [data-section-id]').forEach((e) => {
          if (e.closest('.rte')) return;
          sectionIds.push(e.dataset.sectionId);
        });
      }
      url.searchParams.set('sections', sectionIds.join(','));
      if (combinedProductUrl || !variantId || optionIds.includes(null)) {
        url.searchParams.set('option_values', optionIds.join(','));
      } else {
        url.searchParams.set('variant', variantId);
      }
      return url;
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      // RM: mirror a linked companion dropdown into its buttons (or vice versa) first, so the
      // selection below reflects the change wherever it was made.
      this.syncLinkedSelectors(evt);
      const selectedOptionIds = this.getSelectedOptionIds();

      // Immediately update swatch label
      this.updateSwatchLabel();

      // Construct new variant URL and fetch it, using theme cache
      const combinedProductUrl = evt.target.dataset.productUrl || evt.detail?.productUrl;
      const variantId = evt.target.dataset.variantId || evt.detail?.variantId;
      const url = this.constructVariantUrl(combinedProductUrl, selectedOptionIds, variantId);
      this.variantRequestId += 1;
      const currentRequestId = this.variantRequestId;

      theme.fetchCache.fetch(url.toString())
        .then((responseText) => {
          if (currentRequestId !== this.variantRequestId) return;

          const responseData = JSON.parse(responseText);

          // Update content
          Object.entries(responseData).forEach(([section, htmlStr]) => {
            const html = new DOMParser().parseFromString(htmlStr, 'text/html');
            const sectionId = `shopify-section-${section}`;
            let target = this.section;
            let newContent = html.getElementById(sectionId);

            if (this.section.id !== sectionId) {
              if (!this.quickBuyContainer) {
                target = document.getElementById(sectionId);
              } else {
                const quickBuyTemplate = html.getElementById('quick-buy-template')?.content.querySelector('.js-product');
                if (quickBuyTemplate) newContent = quickBuyTemplate;
              }
            }

            this.updateContent(target, newContent, combinedProductUrl);
          });

          // Restore focus
          if (!evt.detail?.noFocus) {
            document.querySelector(`#${evt.target.id}-button, input#${evt.target.id}`)?.focus();
          }

          // Update URL and announce changes
          const newVariantPicker = this.section.querySelector('variant-picker');
          setTimeout(() => newVariantPicker.announceChange(), 10);
        });
    }

    /**
     * Announces the current variant after a variant change.
     */
    announceChange() {
      const selectedOptionIds = this.getSelectedOptionIds();
      if (!selectedOptionIds.includes(null)) {
        this.updateUrl();
      }

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.getProductForm(),
          variant: this.variant,
          selectedOptions: this.getSelectedOptions()
        }
      }));
    }

    /**
     * Replace content when the variant or product changes.
     * @param {Element} target - Target container for replacing content within.
     * @param {document} newContent - New content containing elements to use.
     * @param {boolean} productChange - Product is changing.
     */
    updateContent(target, newContent, productChange) {
      // If something's not right, do nothing
      if (!target || !newContent) return;

      // Preselection - only update certain option value attributes
      if (this.getSelectedOptions().indexOf(null) !== -1 && !productChange) {
        target.querySelectorAll('.js-option[data-value-id]').forEach((input) => {
          const newInput = (input.id && newContent.querySelector(`#${CSS.escape(input.id)}`))
            || newContent.querySelector(`.js-option[data-value-id="${input.dataset.valueId}"]`);
          if (!newInput) return;
          input.className = newInput.className;
          if (newInput.dataset.variantId) {
            input.dataset.variantId = newInput.dataset.variantId;
          } else {
            delete input.dataset.variantId;
          }
          if (input.classList.contains('custom-select__option')) {
            input.innerHTML = newInput.innerHTML;
          }
        });
        this.updateStatusClasses();
        return;
      }

      let replaceSelector = '[data-dynamic-variant-content]';
      if (productChange) {
        replaceSelector += ', [data-dynamic-product-content]';
      }
      const toReplaceList = target.querySelectorAll(replaceSelector);

      toReplaceList.forEach((toReplace) => {
        const replaceWith = toReplace.dataset.dynamicVariantContent
          ? newContent.querySelector(`[data-dynamic-variant-content="${CSS.escape(toReplace.dataset.dynamicVariantContent)}"]`)
          : newContent.querySelector(`[data-dynamic-product-content="${CSS.escape(toReplace.dataset.dynamicProductContent)}"]`);
        if (!replaceWith) return;

        // Dispatch 'on:variant:before-replace-element' event before replacing element
        document.dispatchEvent(new CustomEvent('on:variant:before-replace-element', {
          bubbles: true,
          detail: {
            toReplace,
            replaceWith
          }
        }));

        // If in quick buy, content needs mutating
        if (this.quickBuyContainer && theme.quickBuy) {
          theme.quickBuy.convertToQuickBuyContent(
            replaceWith,
            this.quickBuyContainer.dataset.productUrl
          );
        }

        // Transfer basic input values
        toReplace.querySelectorAll('input[id]:not([type="hidden"]), select[id]').forEach((sourceInput) => {
          if (sourceInput.matches('.custom-select__native')) {
            const customSelectId = sourceInput.id.replace('-native', '');
            const targetInput = replaceWith.querySelector(`#${CSS.escape(customSelectId)}`);
            const option = targetInput.querySelector(`.js-option[data-value="${CSS.escape(sourceInput.value)}"]`);
            setTimeout(() => {
              targetInput.selectOption(option);
            }, 0);
          } else {
            const targetInput = replaceWith.querySelector(`#${CSS.escape(sourceInput.id)}`);
            if (!targetInput) return;
            if (sourceInput.type === 'radio' || sourceInput.type === 'checkbox') {
              targetInput.toggleAttribute('checked', sourceInput.checked);
            } else {
              targetInput.value = sourceInput.value;
            }
          }
        });

        // Replace content
        const isIdInput = replaceWith.matches('input[name="id"]');
        if (isIdInput) {
          // Update ID input - preserving listeners
          [...replaceWith.attributes].forEach(
            (attr) => toReplace.setAttribute(attr.name, attr.value)
          );
          toReplace.value = replaceWith.value;
          // Defer change event until all forms are accurate
          setTimeout(() => {
            toReplace.dispatchEvent(new Event('change', { bubbles: true }));
          }, 0);
        } else {
          // Remember toReplace becomes detached
          toReplace.replaceWith(replaceWith);
        }

        // Do not mistake input value changes for variant changes - remove listeners
        replaceWith.querySelectorAll('variant-picker').forEach((el) => el.removeListeners());

        // Set values of any unselected <custom-select> elements again
        // A fetched variant may not appear selected
        const selectedOptionIds = this.getSelectedOptionIds();
        selectedOptionIds.forEach((optionId) => {
          const toSelect = replaceWith.querySelector(`.option-selector .custom-select__option[data-value-id="${optionId}"]:not([aria-selected="true"])`);
          if (toSelect) {
            setTimeout(() => {
              toSelect.closest('custom-select').selectOption(toSelect);
            }, 0);
          }
        });

        // Update variant picker after potential changes are applied
        setTimeout(() => {
          replaceWith.querySelectorAll('variant-picker').forEach((el) => {
            el.addListeners();
            el.updateVariantContent();
          });
        }, 10);
      });
    }

    /**
     * RM: Returns the selector that drives the variant for the given selector. For a linked
     * companion dropdown (color family grouping) this is the listed selector of the same option.
     * @param {?Element} selector - Option selector element.
     * @returns {?Element}
     */
    getListedSelector(selector) {
      if (!selector || !selector.matches('[data-selector-ignore]')) return selector;
      return Array.from(this.optionSelectors).find(
        (el) => el.dataset.option === selector.dataset.option
      ) || null;
    }

    /**
     * RM: Keeps a linked companion dropdown and the swatch buttons of the same option in sync.
     * Availability and the selected state of the dropdown are re-rendered by the server after
     * each change, so only the immediate selection needs mirroring here.
     * @param {object} evt - Event object.
     */
    syncLinkedSelectors(evt) {
      const source = evt.target.closest('.option-selector');
      if (!source) return;

      if (source.matches('[data-selector-ignore]')) {
        const listed = this.getListedSelector(source);
        const value = evt.detail ? evt.detail.selectedValue : evt.target.value;
        if (!listed || !value) return;
        const radio = listed.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
        if (radio && !radio.checked) radio.checked = true;
        return;
      }

      const linked = Array.from(this.querySelectorAll('.option-selector[data-selector-ignore]'))
        .find((el) => el.dataset.option === source.dataset.option);
      const customSelect = linked?.querySelector('custom-select');
      if (!customSelect || !evt.target.value) return;
      const option = customSelect.querySelector(`.custom-select__option[data-value="${CSS.escape(evt.target.value)}"]`);
      if (!option || option === customSelect.selectedOption) return;
      customSelect.selectedOption?.setAttribute('aria-selected', 'false');
      option.setAttribute('aria-selected', 'true');
      customSelect.selectedOption = option;
      const btn = customSelect.querySelector('.custom-select__btn');
      if (btn) {
        btn.firstElementChild.textContent = option.firstElementChild.textContent;
        if (btn.dataset.swatch !== undefined) btn.dataset.swatch = option.dataset.swatch || '';
      }
    }

    /**
     * RM: Color family tabs (variant picker "Group Buttons by Color Family" + color_familes
     * metaobjects). Clicking a family shows only its swatches (and dropdown options) and selects
     * the first of them. The chosen family is remembered per section so that it is re-applied
     * after the picker is re-rendered by a variant change.
     */
    initColorFamilies() {
      const tabsWrap = this.querySelector('.color-family-tabs[data-color-families]');
      if (!tabsWrap) return;

      let families = [];
      try {
        families = JSON.parse(tabsWrap.dataset.colorFamilies);
      } catch (err) {
        families = [];
      }

      const normalize = (value) => (value || '').toLowerCase().replace('hwp ', '').trim();
      const fieldset = tabsWrap.closest('.option-selector');
      const swatches = Array.from(fieldset.querySelectorAll('.opt-btn'));
      const linked = Array.from(this.querySelectorAll('.option-selector[data-selector-ignore]'))
        .find((el) => el.dataset.option === fieldset.dataset.option);
      const dropdownOptions = linked ? Array.from(linked.querySelectorAll('.custom-select__option')) : [];
      const tabs = Array.from(tabsWrap.querySelectorAll('[data-family-name]'));

      const familyColors = new Map();
      const grouped = new Set();
      families.forEach((family) => {
        const colors = (family.colors || []).map((c) => c.trim()).filter(Boolean);
        familyColors.set(family.name, colors);
        colors.forEach((c) => grouped.add(c));
      });
      familyColors.set('Others', swatches.map((s) => normalize(s.value)).filter((c) => !grouped.has(c)));

      // Keyed by product as well as section: quick-add pickers share the PDP's section id.
      const sectionId = `${this.dataset.url || ''}|${this.dataset.sectionId}`;
      VariantPicker.colorFamilyState = VariantPicker.colorFamilyState || {};

      const apply = (familyName, selectFirst) => {
        const colors = familyName === 'all' ? null : (familyColors.get(familyName) || []);
        tabs.forEach((tab) => tab.classList.toggle('selected-color-tab', tab.dataset.familyName === familyName));

        let first = null;
        swatches.forEach((input) => {
          const show = !colors || colors.includes(normalize(input.value));
          if (input.nextElementSibling) input.nextElementSibling.style.display = show ? '' : 'none';
          if (show && !first) first = input;
        });
        dropdownOptions.forEach((option) => {
          if (option.dataset.value === '') return;
          option.style.display = !colors || colors.includes(normalize(option.dataset.value)) ? '' : 'none';
        });

        if (selectFirst && colors && first && !first.checked) first.click();
      };

      if (!tabsWrap.dataset.bound) {
        tabsWrap.dataset.bound = 'true';
        tabs.forEach((tab) => {
          tab.addEventListener('click', () => {
            VariantPicker.colorFamilyState[sectionId] = tab.dataset.familyName;
            apply(tab.dataset.familyName, true);
          });
        });
      }

      const remembered = VariantPicker.colorFamilyState[sectionId];
      if (remembered && remembered !== 'all' && tabs.some((tab) => tab.dataset.familyName === remembered)) {
        apply(remembered, false);
      }
    }

    /**
     * RM: Klaviyo back-in-stock. Drives the back-in-stock state through the theme's own buy
     * button instead of Klaviyo's injected one, so it reads like any other variant state.
     *
     * Three outcomes, all on the same button:
     *   - buyable            -> untouched ("Add to cart", enabled)
     *   - unavailable, BIS   -> enabled, labelled from theme.strings.notifyMe,
     *                           clicks forwarded to Klaviyo's hidden trigger
     *   - unavailable, gated -> untouched (the theme's disabled "Sold out"); gated variants have
     *                           the custom.hide_bis variant metafield set
     *
     * Runs after updateAddToCartButton(), which sets the default label and disabled state; this
     * only overrides them for the BIS case. The add-to-cart markup is replaced on every variant
     * change, so the button is resolved on every pass and clicks are delegated from the form.
     * Applies on the product page, and in the quick-add drawer (and the free sample page) where
     * snippets/klaviyo-bis-quick-view.liquid runs Klaviyo in quick-view mode. On product pages a
     * quick-add drawer is skipped: Klaviyo is in product mode there and would open the form for
     * the page's product instead. Not used in featured product sections.
     */
    updateBISButton() {
      if (this.featuredProductContainer) return;
      // Quick-view mode: the quick-add drawer, and product sections shown outside /products/ URLs
      // (the free sample page), where Klaviyo's app embed form does not appear.
      const quickView = Boolean(this.quickBuyContainer) || !/\/products\//.test(window.location.pathname);
      if (quickView && !window.rmKlaviyoBisQuickView) return;
      const productForm = this.getProductForm();
      if (!productForm) return;

      let hideList = window.hideBISVariants;
      if (this.dataset.hideBisVariants !== undefined) {
        hideList = this.dataset.hideBisVariants.split(',').filter(Boolean).map((id) => Number.parseInt(id, 10));
      }
      if (!Array.isArray(hideList)) return;

      const section = this.section;
      const variant = this.variant;
      // Uses this picker's own variant data rather than the form's id input: after a variant
      // change the picker is swapped in before the form's id input has been updated.
      const gated = Boolean(variant) && hideList.includes(Number(variant.id));
      const unbuyable = Boolean(variant) && !variant.available;

      // Klaviyo injects its container asynchronously, so both of these must be resolved on
      // every pass rather than captured once.
      const container = () => (quickView ? null : (productForm.querySelector('#klaviyo-bis-button-container')
        || section?.querySelector('#klaviyo-bis-button-container')));
      const trigger = () => container()?.querySelector('button, a, [role="button"]');

      this.bisObserver?.disconnect();

      const apply = () => {
        const el = container();
        // Whether this variant gets the notify treatment follows from the variant data alone;
        // it deliberately does not wait for Klaviyo to inject its button.
        const canNotify = unbuyable && !gated;
        // Fall back to Klaviyo's own button only once theirs has arrived without anything
        // we can click.
        const fallback = canNotify && Boolean(el) && !trigger();

        if (el) {
          if (fallback) {
            // Guarded: an unconditional write would re-trigger the observer forever.
            if (el.style.display) el.style.removeProperty('display');
          } else if (el.style.display !== 'none') {
            // Plain inline style, not setProperty with 'important': per-template stylesheets
            // test this attribute for the literal substring "display: none;".
            el.style.display = 'none';
          }
        }

        productForm.classList.toggle('bis-unbuyable', unbuyable);
        productForm.classList.toggle('bis-notify', canNotify && !fallback);
        productForm.classList.toggle('bis-fallback', fallback);

        const addBtn = productForm.querySelector('[name="add"]');
        if (!addBtn) return;
        if (canNotify && !fallback) {
          if (addBtn.disabled) addBtn.disabled = false;
          if (addBtn.type !== 'button') addBtn.type = 'button';
          // Every write here is conditional: textContent replaces child nodes, which the
          // observer below would see, making this function trigger itself forever.
          if (addBtn.textContent !== theme.strings.notifyMe) {
            addBtn.textContent = theme.strings.notifyMe;
          }
        } else if (addBtn.type !== 'submit') {
          addBtn.type = 'submit';
        }
      };

      apply();

      if (!productForm.dataset.bisClickBound) {
        productForm.dataset.bisClickBound = 'true';
        productForm.addEventListener('click', (evt) => {
          if (!evt.target.closest('[name="add"]')) return;
          if (!productForm.classList.contains('bis-notify')) return;
          evt.preventDefault();
          evt.stopPropagation();
          if (quickView) {
            this.openQuickViewBIS(productForm);
            return;
          }
          const bisTrigger = productForm.querySelector('#klaviyo-bis-button-container button, #klaviyo-bis-button-container a, #klaviyo-bis-button-container [role="button"]')
            || productForm.closest('.shopify-section')?.querySelector('#klaviyo-bis-button-container button, #klaviyo-bis-button-container a, #klaviyo-bis-button-container [role="button"]');
          bisTrigger?.click();
        }, true);
      }

      // Watch the section subtree so Klaviyo injecting its container - or hiding its own
      // button - and the buy button being re-rendered are picked up whenever they happen.
      // MutationObserver callbacks run before paint, so a freshly swapped-in "Sold out" button
      // is relabelled before it is ever shown. Every write above is idempotent.
      this.bisObserver = new MutationObserver(apply);
      this.bisObserver.observe(section || productForm, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style']
      });
    }

    /**
     * RM: disables the quantity selector while the selected variant cannot be bought, on the
     * product page and in the quick-add drawer. Only controls this method disabled are
     * re-enabled, so the theme's own disabled state (e.g. the plus button at max quantity) is kept.
     * The add-to-cart markup is re-rendered on variant change, so this runs after every update.
     */
    updateQuantityState() {
      const productForm = this.getProductForm();
      if (!productForm) return;
      const unbuyable = Boolean(this.variant) && !this.variant.available;

      productForm.querySelectorAll('quantity-input').forEach((qty) => {
        qty.classList.toggle('is-disabled', unbuyable);
        qty.setAttribute('aria-disabled', unbuyable ? 'true' : 'false');
        qty.querySelectorAll('input, button').forEach((el) => {
          if (unbuyable && !el.disabled) {
            el.disabled = true;
            el.dataset.rmQtyDisabled = 'true';
          } else if (!unbuyable && el.dataset.rmQtyDisabled) {
            el.disabled = false;
            delete el.dataset.rmQtyDisabled;
          }
        });
      });
    }

    /**
     * RM: opens Klaviyo's back-in-stock form from the quick-add drawer. Klaviyo (quick-view mode,
     * see snippets/klaviyo-bis-quick-view.liquid) listens for clicks on .klaviyo-bis-trigger
     * anywhere in the page, loads /products/<data-klaviyo-handle>.js and preselects the variant
     * whose id it reads from the element data-klaviyo-quickview-variant selects.
     * @param {Element} productForm - The quick-add product form.
     */
    openQuickViewBIS(productForm) {
      const idInput = productForm.querySelector('input[name="id"]');
      const url = this.quickBuyContainer?.dataset.productUrl || this.dataset.url || '';
      const handle = (url.split('?')[0].match(/\/products\/([^/]+)/) || [])[1];
      if (!idInput || !handle) return;
      if (this.variant && String(idInput.value) !== String(this.variant.id)) idInput.value = this.variant.id;
      if (!idInput.id) idInput.id = `rm-bis-variant-${Date.now()}`;

      let bisTrigger = productForm.querySelector('a.klaviyo-bis-trigger.rm-bis-quick-view');
      if (!bisTrigger) {
        bisTrigger = document.createElement('a');
        bisTrigger.href = '#';
        bisTrigger.className = 'klaviyo-bis-trigger rm-bis-quick-view';
        bisTrigger.hidden = true;
        productForm.appendChild(bisTrigger);
      }
      bisTrigger.setAttribute('data-klaviyo-handle', decodeURIComponent(handle));
      bisTrigger.setAttribute('data-klaviyo-quickview-variant', `#${idInput.id}`);
      bisTrigger.click();
    }

    /**
     * Set status classes to help with styling.
     */
    updateStatusClasses() {
      const selectedOpts = this.getSelectedOptionIds();
      const nullCount = selectedOpts.filter((x) => x === null).length;
      const isSingleNullAtEnd = nullCount === 1 && selectedOpts[selectedOpts.length - 1] === null;
      this.classList.toggle('variant-picker--preselection', nullCount > 0);
      this.classList.toggle('variant-picker--pre-last-selection', nullCount > 0 && !isSingleNullAtEnd);
    }

    /**
     * Copies the selected value into a swatch label. (Required for pre-selection.)
     */
    updateSwatchLabel() {
      this.querySelectorAll('.option-selector:has(.js-color-text)').forEach((option) => {
        const label = option.querySelector('.js-color-text');
        const input = option.querySelector('.js-option:is(:checked, [aria-selected="true"])');
        label.textContent = input ? input.value : '';
      });
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.availability === 'prune') {
        const toChange = this.querySelectorAll('.js-option.is-unavailable:is(:checked, [aria-selected="true"])');
        toChange.forEach((selected) => {
          const toSelect = selected.closest('.option-selector').querySelector('.js-option:not(.is-unavailable, [data-value=""])');
          if (toSelect) {
            setTimeout(() => {
              if (toSelect.closest('custom-select')) {
                toSelect.closest('custom-select').selectOption(toSelect);
              } else {
                toSelect.click();
              }
            }, 20); // Trigger after any updateContent logic has occurred
          }
        });
      }
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      const productForm = this.getProductForm();
      if (!productForm) return;

      this.addBtn = productForm.querySelector('[name="add"]');

      // Product not available
      if (!this.productAvailable) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noStock;
        return;
      }

      // Preselection
      if (this.getSelectedOptions().indexOf(null) !== -1) {
        if (this.addBtn.dataset.preselectionDisabled === 'true') {
          this.addBtn.disabled = true;
          this.addBtn.textContent = this.addBtn.dataset.preselectionText;
        } else {
          this.addBtn.disabled = false;
          this.addBtn.textContent = this.addBtn.dataset.addToCartText;
        }
        return;
      }

      // No variant
      if (!this.variant) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noVariant;
        return;
      }

      // Sold out
      if (!this.variant.available) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noStock;
        return;
      }

      // Add to cart
      this.addBtn.disabled = false;
      this.addBtn.textContent = this.addBtn.dataset.addToCartText;
    }

    /**
     * Updates the url with the selected variant id.
     */
    updateUrl() {
      if (this.dataset.updateUrl === 'false') return;
      const url = this.variant ? `${this.dataset.url}?variant=${this.variant.id}` : this.dataset.url;
      window.history.replaceState({ }, '', url);
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.section.querySelector('variant-picker [type="application/json"]');
      return dataEl ? JSON.parse(dataEl.textContent) : null;
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get the selected option value ids from a list of variant options.
     * @returns {Array} Array of selected option value ids, value is null if not selected.
     */
    getSelectedOptionIds() {
      const selectedOptionIds = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptionIds.push(selected && selected.dataset.value !== '' ? selected.dataset.valueId : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptionIds.push(selected ? selected.dataset.valueId : null);
        }
      });

      return selectedOptionIds;
    }

    /**
     * Apply search parameters (set by product card swatches).
     */
    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find((x) => x.dataset.option === key);
        if (!matchingOptionSelector) return;

        if (matchingOptionSelector.dataset.selectorType === 'dropdown') {
          const colorOptionDropdown = matchingOptionSelector.querySelector(`.custom-select__option[data-value="${CSS.escape(value)}"]:not([aria-selected="true"])`);
          if (colorOptionDropdown) {
            const customSelect = colorOptionDropdown.closest('custom-select');
            const convertEvent = (evt) => {
              evt.detail.noFocus = true;
              customSelect.dispatchEvent(
                new CustomEvent('change', { bubbles: true, cancelable: false, detail: evt.detail })
              );
            };
            customSelect.addEventListener('change', convertEvent, { once: true });
            customSelect.selectOption(colorOptionDropdown);
          }
        } else {
          const matchingInput = matchingOptionSelector.querySelector(`input[value="${CSS.escape(value)}"]:not(:checked)`);
          if (matchingInput) {
            const matchingOptionValue = matchingOptionSelector.querySelector('.option-selector__label-value');
            if (matchingOptionValue) {
              matchingOptionSelector.querySelector('.option-selector__label-value').textContent = value;
            }
            matchingInput.checked = true;
            matchingInput.dispatchEvent(
              new CustomEvent('change', { bubbles: true, cancelable: false, detail: { noFocus: true } })
            );
          }
        }
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
